import type { PayloadAction } from "@reduxjs/toolkit";
import { createSelector, createSlice } from "@reduxjs/toolkit";
import { CalendarData } from "../calendar/model";
import { RootState } from "./store";
import {
    DayProps,
    defaultConsultation,
    IndividualDay,
} from "../modal/ConsultationModals/Content/Day";
import { defaultValue } from "../modal/ConsultationModals/Content/DayRow/ConsultationInputs/RoomSelect";
import copyFromRegularToIndividuals, {
    getEvenOdd,
} from "./copyFromRegularToIndividuals";
import { addMinutes, areIntervalsOverlapping } from "date-fns";
import { getCurrentWeek } from "../calendar/carouselCalendar/legacyUtils";
import _set from "lodash/set";
import _setWith from "lodash/setWith";
import _last from "lodash/last";
import _isEqual from "lodash/isEqual";

export interface State {
    calendarData: CalendarData;
    untouchedData: CalendarData;
    regularChanged: boolean;
    individualChanged: boolean;
    showChangeList: boolean;
}

const emptyCalendarData: CalendarData = {
    regularWeekSettings: {
        oddWeekUsed: false,
        even: {},
        odd: {},
    },
    individualWeekSettings: {},
    otherReservations: [],
    reservations: [],
    rooms: [],
    specializations: [],
};

const initialState: State = {
    calendarData: emptyCalendarData,
    untouchedData: emptyCalendarData,
    regularChanged: false,
    individualChanged: false,
    showChangeList: false,
};

const slice = createSlice({
    name: "data",
    initialState,
    reducers: {
        initData: (
            state,
            { payload: initialCalendarData }: PayloadAction<CalendarData>
        ) => {
            return {
                calendarData: initialCalendarData,
                untouchedData: initialCalendarData,
                regularChanged: false,
                individualChanged: false,
                showChangeList: false,
            };
        },

        resetData: (state) => {
            state.calendarData = state.untouchedData;
            state.regularChanged = false;
            state.individualChanged = false;
            state.showChangeList = false;
        },
        toggleOddWeekUsed: (state) => {
            state.regularChanged = true;
            const newState =
                !state.calendarData.regularWeekSettings.oddWeekUsed;
            const regularWeekSettings = state.calendarData.regularWeekSettings;

            regularWeekSettings.oddWeekUsed = newState;

            if (newState) {
                // Copy from even to odd weeks
                state.calendarData.regularWeekSettings.odd =
                    state.calendarData.regularWeekSettings.even;
            }

            // Copy regular even to odd individual weeks both when turning on or off odd week usage
            const individualWeekSettings =
                state.calendarData.individualWeekSettings;

            const currentWeek = getCurrentWeek();
            const currentYear = new Date().getFullYear();

            for (let week = 0; week <= 52; week++) {
                // Iterate through 52 weeks from current one
                const year =
                    week >= currentWeek ? currentYear : currentYear + 1;

                if (getEvenOdd(week) === "even") {
                    // Leave even week overrides alone
                    continue;
                }

                for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
                    const consultations = regularWeekSettings.even[dayIndex];

                    if (!consultations) {
                        if (
                            !individualWeekSettings?.[year]?.[week]?.[dayIndex]
                        ) {
                            continue;
                        }

                        delete individualWeekSettings[year][week][dayIndex];

                        continue;
                    }

                    _set(
                        individualWeekSettings,
                        [year, week, dayIndex],
                        consultations
                    );
                }
            }
        },
        cancelReservation: (
            state,
            { payload: reservationId }: PayloadAction<number>
        ) => {
            state.calendarData.reservations =
                state.calendarData.reservations.filter(
                    (reservation) => reservation.id !== reservationId
                );
        },
        setShowChangeList: (
            state,
            { payload: show }: PayloadAction<boolean>
        ) => {
            state.showChangeList = show;
        },
        setCalendarData: (
            state,
            { payload: newCalendarData }: PayloadAction<CalendarData>
        ) => {
            state.calendarData = newCalendarData;
        },
        setRoom: (
            state,
            {
                payload: { day, consultationIndex, roomId },
            }: PayloadAction<{
                day: DayProps;
                consultationIndex: number;
                roomId: number;
            }>
        ) => {
            if (day.isRegular) {
                state.regularChanged = true;
            } else {
                state.individualChanged = true;
            }

            const consultation = day.isRegular
                ? state.calendarData.regularWeekSettings[day.weekType][
                      day.index
                  ][consultationIndex]
                : state.calendarData.individualWeekSettings[day.year][day.week][
                      day.index
                  ][consultationIndex];

            consultation.room_id = roomId.toString();
            // copyFromRegularToIndividuals(state, day);
        },
        setSpecializations: (
            state,
            {
                payload: { day, consultationIndex, specializationIds },
            }: PayloadAction<{
                day: DayProps;
                consultationIndex: number;
                specializationIds: number[];
            }>
        ) => {
            if (day.isRegular) {
                state.regularChanged = true;
            } else {
                state.individualChanged = true;
            }

            const consultation = day.isRegular
                ? state.calendarData.regularWeekSettings[day.weekType][
                      day.index
                  ][consultationIndex]
                : state.calendarData.individualWeekSettings[day.year][day.week][
                      day.index
                  ][consultationIndex];

            consultation.specialization_id = specializationIds;
            // copyFromRegularToIndividuals(state, day);
        },
        setTimeRange: (
            state,
            {
                payload: { day, consultationIndex, start, end },
            }: PayloadAction<{
                day: DayProps;
                consultationIndex: number;
                start: string;
                end: string;
            }>
        ) => {
            if (day.isRegular) {
                state.regularChanged = true;
            } else {
                state.individualChanged = true;
            }
            const consultation = day.isRegular
                ? state.calendarData.regularWeekSettings[day.weekType][
                      day.index
                  ][consultationIndex]
                : state.calendarData.individualWeekSettings[day.year][day.week][
                      day.index
                  ][consultationIndex];

            consultation.start = start;
            consultation.end = end;
            // copyFromRegularToIndividuals(state, day);
        },
        addDay: (state, { payload: day }: PayloadAction<DayProps>) => {
            if (day.isRegular) {
                state.regularChanged = true;
                _set(
                    state.calendarData.regularWeekSettings,
                    [day.weekType, day.index],
                    [defaultConsultation]
                );
            } else {
                state.individualChanged = true;
                _setWith(
                    state.calendarData.individualWeekSettings,
                    [day.year, day.week, day.index],
                    [defaultConsultation],
                    Object
                );
            }

            // copyFromRegularToIndividuals(state, day);
        },
        removeDay: (state, { payload: day }: PayloadAction<DayProps>) => {
            if (day.isRegular) {
                state.regularChanged = true;
            } else {
                state.individualChanged = true;
            }
            const days = day.isRegular
                ? state.calendarData.regularWeekSettings[day.weekType]
                : state.calendarData.individualWeekSettings[day.year][day.week];

            delete days[day.index];
            // copyFromRegularToIndividuals(state, day);
        },
        addConsultation: (state, { payload: day }: PayloadAction<DayProps>) => {
            if (day.isRegular) {
                state.regularChanged = true;
            } else {
                state.individualChanged = true;
            }
            const consultations = day.isRegular
                ? state.calendarData.regularWeekSettings[day.weekType][
                      day.index
                  ]
                : state.calendarData.individualWeekSettings[day.year][day.week][
                      day.index
                  ];

            consultations.push({
                ...defaultConsultation,
                start: _last(consultations)?.end ?? "08:00",
            });
            // copyFromRegularToIndividuals(state, day);
        },
        removeConsultation: (
            state,
            {
                payload: { day, consultationIndex },
            }: PayloadAction<{ day: DayProps; consultationIndex: number }>
        ) => {
            if (day.isRegular) {
                state.regularChanged = true;
            } else {
                state.individualChanged = true;
            }
            const days = day.isRegular
                ? state.calendarData.regularWeekSettings[day.weekType]
                : state.calendarData.individualWeekSettings[day.year][day.week];

            days[day.index].splice(consultationIndex, 1);
            // copyFromRegularToIndividuals(state, day);
        },
    },
});

export const dataReducer = slice.reducer;

export const {
    initData,
    resetData,
    toggleOddWeekUsed,
    cancelReservation,
    setRoom,
    setSpecializations,
    setTimeRange,
    addDay,
    removeDay,
    addConsultation,
    removeConsultation,
    setShowChangeList,
    setCalendarData,
} = slice.actions;

export const getCalendarData = (state: RootState) => state.data.calendarData;

export const getOriginalData = (state: RootState) => state.data.untouchedData;

export const getDailyConsultations =
    ({ isRegular, weekType, index, year, week }: DayProps) =>
    (state: RootState) => {
        const calendarData = state.data.calendarData;

        return isRegular
            ? calendarData.regularWeekSettings[weekType]?.[index]
            : calendarData.individualWeekSettings?.[year]?.[week]?.[index];
    };

export const getDailyIndividualConsultations =
    ({ index, year, week }: Pick<IndividualDay, "index" | "year" | "week">) =>
    (state: RootState) => {
        const calendarData = state.data.calendarData;

        return calendarData.individualWeekSettings?.[year]?.[week]?.[index];
    };

export const isDayEmpty = (day: DayProps) =>
    createSelector(
        getDailyConsultations(day),
        (dailyConsultations) =>
            !dailyConsultations || dailyConsultations.length === 0
    );

export const isOddWeekUsed = (state: RootState) =>
    state.data.calendarData.regularWeekSettings.oddWeekUsed;

export const getRooms = (state: RootState) => state.data.calendarData.rooms;
export const getRoomValue = (roomId: number) =>
    createSelector(getRooms, (rooms) =>
        roomId < 0
            ? defaultValue
            : {
                  value: roomId,
                  label: rooms.find((room) => room.id === roomId)?.name || "",
              }
    );

export const getSpecializations = (state: RootState) =>
    state.data.calendarData.specializations;
export const getSpecializationValues = (specializationIds: number[]) =>
    createSelector(getSpecializations, (specializations) =>
        specializationIds?.map((id) => ({
            value: id,
            label:
                specializations.find((spec) => spec.specialization_id === id)
                    ?.specialization || "",
        }))
    );

export const isDayDisabled = (day: DayProps) => (state: RootState) =>
    day.isRegular
        ? !state.data.calendarData.regularWeekSettings[day.weekType]?.[
              day.index
          ]
        : !state.data.calendarData.individualWeekSettings?.[day.year]?.[
              day.week
          ]?.[day.index];

export const getReservationsInSlot =
    (time: Date, intervalLength: number) => (state: RootState) =>
        state.data.calendarData.reservations.filter((reservation) =>
            areIntervalsOverlapping(
                { start: time, end: addMinutes(time, intervalLength) },
                {
                    start: new Date(`${reservation.date} ${reservation.start}`),
                    end: new Date(`${reservation.date} ${reservation.end}`),
                }
            )
        );

export const getRestrictedReservationsInSlot =
    (time: Date, intervalLength: number) => (state: RootState) =>
        state.data.calendarData.otherReservations.filter((reservation) =>
            areIntervalsOverlapping(
                { start: time, end: addMinutes(time, intervalLength) },
                {
                    start: new Date(`${reservation.date} ${reservation.start}`),
                    end: new Date(`${reservation.date} ${reservation.end}`),
                }
            )
        );

export const isDirty = (state: RootState) =>
    !_isEqual(state.data.calendarData, state.data.untouchedData);

export const isRegularChanged = (state: RootState) => state.data.regularChanged;

export const isIndividualChanged = (state: RootState) =>
    state.data.individualChanged;

export const showChangeList = (state: RootState) => state.data.showChangeList;
